import TablePagination from '@material-ui/core/TablePagination';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import styled from 'styled-components';
import { Map } from 'immutable';

import { fetchingState } from '@constants';
import AgGrid from '@components/AgGrid/AgGrid';

import * as globalActions from '../../actions/global';
import * as propertyActions from '../../actions/property';
import NumberRenderer from '../../components/numberRenderer';
import SimpleLinkRenderer from '../../components/simpleLinkRenderer';
import SearchForm from './searchForm';
import { propertyLookupModes } from '../../constants';
import selector from './selector';
import { showError, showSuccess } from '@actions/global';
import { setLastOrderColumnsR, setVisibleColumnsR, setWidthR } from '@utils';
import * as R from 'ramda';

const colSizes = {
  pid: 110,
  pYear: 70,
  propType: 100,
  legalDescription: 270,
  asCode: 100,
  block: 80,
  lot: 80,
  dba: 200,
  streetPrimary: 150,
};

const mergeColumns = (primaryCols, cols) => {
  if (!primaryCols || !primaryCols.length) return cols;
  const primaryColIds = primaryCols.map(c => c.id);
  return [...primaryCols, ...cols.filter(c => !primaryColIds.includes(c.id))];
};

let yearMode = false;

const PropertyLookupModal = ({
  addPropertyToBP,
  addPropertyToSG,
  appraisalYears,
  closeModal,
  columns,
  config,
  currentAppraisalYear,
  data,
  dropdowns,
  fetchDropdowns,
  fetchAppraisalYears,
  fetchSearchConfig,
  fetchSearchResultsConfig,
  fetchPreference,
  persistPreference,
  lookupProperty,
  modalOpen,
  mode,
  marketLookup,
  currentValuation,
  preference,
  recordCount,
  searchParams,
  selectProperties,
  state,
  userData,
  values,
  primarySearchFields,
  primaryResultFields,
  fetchPropertySalesListing,
  fetchMarketApproachValuation,
  fetchMarketApproachCompImages,
  marketApproachManualReset,
  marketApproachAddCompList,
  marketApproach,
  futureYear,
}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [advSearchOpts, setAdvSearchOpts] = useState([]);

  const [selectedItems, setSelectedItems] = useState([]); // eslint-disable-line no-unused-vars

  const [saleSelectShow, setSaleSelectShow] = useState(false);
  const [propertySales, setPropertySales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);

  const agGridApi = useRef();
  const codeFilesFetched = useRef(false);
  const selectedItemsByPageRef = useRef();
  const selectedPropertiesRef = useRef();
  const selectedSaleRef = useRef();

  if (!primaryResultFields || !primaryResultFields.length) {
    primaryResultFields = [
      { id: 'pid', displayName: 'Property ID' },
      { id: 'pYear', displayName: 'Year' },
      { id: 'propType', displayName: 'Property Type' },
      { id: 'streetPrimary', displayName: 'Street Primary' },
    ];
  }
  const visibleColumns =
    visibleColumns && visibleColumns.length
      ? _.union(
          preference
            ? preference.getIn(['data', 'propertySearch', 'visibleColumns'])
            : [],
          primaryResultFields ? primaryResultFields.map(f => f.id) : [],
        )
      : [];
  const valuesJs = (values && values.toJS()) || {};

  const topSearchFields = primarySearchFields.length
    ? primarySearchFields
    : [
        { name: 'pid', label: 'Prop ID', operator: '=' },
        { name: 'name', label: 'Name', operator: 'match' },
        { name: 'streetPrimary', label: 'Situs', operator: 'match' },
      ];

  const mergedColumns = mergeColumns(primaryResultFields, columns);

  useEffect(() => {
    if (rowsPerPage && agGridApi.current) {
      performSearch();
    }
  }, [rowsPerPage]);

  useEffect(() => {
    fetchPreference({
      userId: userData.get('id'),
    });
    fetchSearchResultsConfig();
    fetchSearchConfig();
  }, []);

  useEffect(() => {
    if (searchParams) {
      setAdvSearchOpts(searchParams);
    }
    if (modalOpen) {
      if (primarySearchFields && primarySearchFields.length > 0) {
        for (let x = 0; x < primarySearchFields.length; x++) {
          if (primarySearchFields[x].name === 'pYear') {
            yearMode = true;
          }
        }
      } else {
        yearMode = false;
      }
    }
  }, [modalOpen]);

  useEffect(() => {
    if (config && config.length && !codeFilesFetched.current && modalOpen) {
      config.forEach(({ id, codefile }) => {
        if (codefile) {
          if (dropdowns && dropdowns.getIn(['state', id])) {
            return;
          }
          if (
            ['asCode', 'marketArea', 'abstractSubdivisionName'].includes(id)
          ) {
            return;
          }
          fetchDropdowns({
            fieldName: id,
            endpoint:
              `/appraisal/codes?type=${codefile.codeFileType}` +
              `&name=${codefile.codeFileName}`,
            year:
              codefile.codeFileName !== 'Tags'
                ? codefile.byYear && currentAppraisalYear
                : 0,
          });
        }
      });
      fetchDropdowns({
        fieldName: 'taxingUnitCode',
        endpoint: '/assessment/taxingunitssummary',
        service: 'assessmentService',
      });
      fetchDropdowns({
        fieldName: 'exemptionCode',
        endpoint: `/assessment/exemptions/${currentAppraisalYear}`,
        service: 'assessmentService',
      });
      fetchAppraisalYears();
      codeFilesFetched.current = true;
    }
  }, [config, modalOpen]);

  const closeProcedure = () => {
    setPage(1);
    setRowsPerPage(200);
    setSelectedItems([]);
    selectedItemsByPageRef.current = [];
    setSaleSelectShow(false);
    selectedPropertiesRef.current = [];
    setPropertySales([]);
    selectedSaleRef.current = [];
    closeModal();
  };

  const nextProcedure = async () => {
    const selected = selectedPropertiesRef.current || [];
    if (selected?.length > 0) {
      setSaleSelectShow(true);
      setLoadingSales(true);
      const res = await fetchPropertySalesListing({
        pID: selected[0].pid,
      });
      if (res && res?.results && res.results?.length > 0) {
        setPropertySales(res.results);
      }
      setLoadingSales(false);
    }
  };

  const SelectSales = async () => {
    const selected = selectedSaleRef.current || [];
    if (selected?.length > 0 && currentValuation) {
      const payload = {
        saleIDList: [selected[0].saleID],
      };
      closeProcedure();
      let compLength = marketApproach?.compGridData?.comparables?.length;
      const addData = await marketApproachAddCompList({
        pMarketId: currentValuation,
        payload: payload,
      });

      if (
        addData &&
        addData.results &&
        addData.results[0] &&
        addData.results[0].compGridData &&
        addData.results[0].compGridData.comparables.length > compLength
      ) {
        dispatch(showSuccess('Property sale added to comp grid'));
        fetchMarketApproachValuation({ pMarketId: currentValuation });
        fetchMarketApproachCompImages({ pMarketId: currentValuation });
        marketApproachManualReset();
      } else {
        dispatch(showError('Error adding property sale to comp grid'));
      }
    }
    closeProcedure();
  };

  const addAdvancedSearchOption = ({ field, operator, value }) => {
    setAdvSearchOpts([
      ...advSearchOpts,
      {
        field,
        operator,
        value,
      },
    ]);
  };

  const removeAdvancedSearchOption = idx =>
    setAdvSearchOpts([
      ...advSearchOpts.slice(0, idx),
      ...advSearchOpts.slice(idx + 1),
    ]);

  const performSearch = () => {
    selectedPropertiesRef.current = [];
    setPropertySales([]);
    setSaleSelectShow(false);
    let pYearData = null;
    if (valuesJs && valuesJs.pYear) {
      pYearData = valuesJs.pYear;
      delete valuesJs.pYear;
    }
    let searchParams = Object.entries(valuesJs).reduce((acc, [key, val]) => {
      if (topSearchFields.map(f => f.name).includes(key)) {
        const operator = config.find(c => c.id === key).operators[0];
        let value = val;
        if (operator === 'in') {
          value = [val];
        } else if (operator === '#') {
          value = val
            .trim()
            .split(/[ ,]+/)
            .filter(item => item);
        }
        acc[key] = {
          operator,
          value,
        };
      }
      return acc;
    }, {});
    if (pYearData) {
      searchParams.pYear = { operator: '=', value: pYearData };
    } else {
      searchParams.pYear = { operator: '=', value: futureYear ? 9999 : currentAppraisalYear };
    }
    advSearchOpts.forEach(({ field, operator, value }) =>
      operator !== 'between'
        ? (searchParams[field] = { operator, value })
        : (searchParams[field] = {
            operator,
            beginValue: value[0],
            endValue: value[1],
          }),
    );
    lookupProperty({
      page,
      pageSize: rowsPerPage,
      payload: searchParams,
    });
  };

  const submitValues = (newSelectedItems = []) => {
    let properties = [];
    setSelectedItems(prev => {
      const currentProps = [
        ...prev,
        ...selectedItemsByPageRef.current || [],
        ...newSelectedItems,
      ];
      // properties = currentProps.map(row => ({
      //   pid: row.pid,
      //   pYear: row.pYear,
      //   pVersion: row.pVersion,
      //   pRollCorr: row.pRollCorr,
      // }));
      properties = currentProps.map(row => row);
      return _.uniqBy(currentProps, 'pid');
    });

    addPropertyToBP &&
      addPropertyToBP({ pId: properties[properties.length - 1].pid });
    addPropertyToSG &&
      addPropertyToSG({
        pId: properties[properties.length - 1].pid,
        pYear: properties[properties.length - 1].pYear,
      });
    selectProperties(properties);
    if (addPropertyToSG) return;

    closeProcedure();
  };

  const buildColumnDefs = () => {
    if (!columns) {
      return [];
    }

    let columnDefs = mergedColumns.map(field => ({
      headerName: field.displayName,
      field: field.id,
      width: colSizes[field.id],
      // maxWidth: colSizes[field.id],
      tooltip: params => params.value,
      cellRenderer: field.needFormatting ? NumberRenderer : undefined,
    }));

    columnDefs = columnDefs.map(column => {
      if (column.field === 'pid') {
        return {
          ...column,
          cellRenderer: SimpleLinkRenderer,
          cellRendererParams: {
            onClick: (value, record) => {
              if (marketLookup) {
                selectedPropertiesRef.current = [record];
              } else {
                if (!addPropertyToSG) {
                  selectProperties([record]);
                  closeProcedure();
                }
              }
            },
          },
        };
      }
      return column;
    });

    if (visibleColumns && visibleColumns.length) {
      columnDefs = columnDefs.filter(column =>
        visibleColumns.includes(column.field),
      );
    }

    const visibleColumns = preference.getIn([
      'data',
      'propertyLookup',
      'visibleColumns',
    ]);
    const movedColumns = preference.getIn([
      'data',
      'propertyLookup',
      'movedColumns',
    ]);
    const columnsWidth = preference.getIn([
      'data',
      'propertyLookup',
      'columnsWidth',
    ]);

    columnDefs = R.compose(
      setWidthR(columnsWidth),
      setLastOrderColumnsR(movedColumns),
      setVisibleColumnsR(visibleColumns),
    )(columnDefs);

    columnDefs = [
      {
        field: 'select',
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 90,
        maxWidth: 90,
        resizable: false,
        suppressSizeToFit: true,
        pinned: 'left',
      },
      ...columnDefs,
    ];

    return columnDefs;
  };

  const buildColumnDefsSale = () => {
    const baseColumns = [
      {
        headerName: 'Sale ID',
        field: 'saleID',
      },
      {
        headerName: 'Sale Date',
        field: 'saleDt',
      },
      {
        headerName: 'Sale Type',
        field: 'saleType',
      },
      {
        headerName: 'Sale Adjusted Price',
        field: 'salePriceAdjusted',
      },
      {
        headerName: 'Living Area',
        field: 'imprvMainArea',
      },
      {
        headerName: 'Class',
        field: 'imprvClass',
      },
      {
        headerName: 'Sale Ratio',
        field: 'saleRatio',
      },
      {
        headerName: 'Situs',
        field: 'situs',
      },
    ];

    const visibleColumns = preference.getIn([
      'data',
      'selectPropertySales',
      'visibleColumns',
    ]);
    const movedColumns = preference.getIn([
      'data',
      'selectPropertySales',
      'movedColumns',
    ]);
    const columnsWidth = preference.getIn([
      'data',
      'selectPropertySales',
      'columnsWidth',
    ]);

    const result = R.compose(
      setWidthR(columnsWidth),
      setLastOrderColumnsR(movedColumns),
      setVisibleColumnsR(visibleColumns),
    )(baseColumns);

    result.unshift({
      field: 'select',
      headerName: '',
      checkboxSelection: true,
      width: 60,
      maxWidth: 60,
      resizable: false,
      suppressSizeToFit: true,
      pinned: 'left',
    });

    return result;
  };

  const buildRowData = useMemo(() => {
    if (!data || !data.toJS) {
      return [];
    }

    return data.toJS().map((property, index) => {
      const value = {};
      mergedColumns.forEach(field => {
        value[field.id] = property[field.id];
      });
      value.pVersion = property.pVersion;
      value.pRollCorr = property.pRollCorr;
      value.pYear = property.pYear;
      value.seq = index + 1;
      return value;
    });
  }, [data]);


  const handleSelectionChanged = useCallback(event => {
    selectedItemsByPageRef.current = event.api.getSelectedRows();
    selectedPropertiesRef.current = event.api.getSelectedRows();
  }, []);

  const handleSelectionSaleChanged = event => {
    selectedSaleRef.current = event.api.getSelectedRows();
  };

  const nextToSale = async data => {
    setSaleSelectShow(true);
    setLoadingSales(true);
    const res = await fetchPropertySalesListing({
      pID: data.pid,
    });
    if (res && res?.results && res.results?.length > 0) {
      setPropertySales(res.results);
    }
    setLoadingSales(false);
  };

  const onRowDataUpdated = param => {
    param.api.forEachNode((node, key) => {
      if (key === 0) {
        node.setSelected(true);
        selectedPropertiesRef.current = [node.data];
        if (marketLookup) {
          nextToSale(node.data);
        }
        return;
      }
    });
  };

  const onSaleRowDataChanged = param => {
    param.api.forEachNode((node, key) => {
      if (key === 0) {
        node.setSelected(true);
        selectedSaleRef.current = [node.data];
        return;
      }
    });
  };

  return (
    <Dialog maxWidth={false} open={modalOpen} onClose={closeProcedure}>
      <DialogTitle id="form-dialog-title">
        Property Lookup
        <CloseButton onClick={closeProcedure}>
          <CloseIcon />
        </CloseButton>
      </DialogTitle>
      <DialogContent
        style={{
          paddingBottom: 0,
          maxWidth: 'none',
          width: '1100px',
          overflowX: 'hidden',
        }}
      >
        <SearchForm
          config={config}
          dropdowns={dropdowns}
          onSubmit={performSearch}
          formValues={valuesJs}
          addAdvSearchOpt={addAdvancedSearchOption}
          removeAdvSearchOpt={removeAdvancedSearchOption}
          advSearchOpts={advSearchOpts}
          primarySearchFields={topSearchFields}
          appraisalYears={appraisalYears}
          initialValues={
            yearMode ? new Map({ pYear: currentAppraisalYear }) : {}
          }
        />
        {!marketLookup ? (
          <>
            <TableContainer className="ag-theme-material">
              {state === fetchingState.FETCHING ? (
                <div style={{ textAlign: 'center' }}>
                  <CircularProgress />
                </div>
              ) : (
                <AgGrid
                  name="propertyLookup"
                  rowHeight={38}
                  columnDefs={buildColumnDefs()}
                  rowData={buildRowData || []}
                  fetchPreference={fetchPreference}
                  persistPreference={persistPreference}
                  preference={preference}
                  enableBrowserTooltips
                  defaultColDef={{ resizable: true, sortable: true }}
                  onSelectionChanged={handleSelectionChanged}
                  rowSelection={
                    mode === propertyLookupModes.singleSelection
                      ? 'single'
                      : 'multiple'
                  }
                  rowMultiSelectWithClick={
                    mode === propertyLookupModes.multiSelection
                  }
                  onGridReady={params => {
                    params.columnApi.autoSizeAllColumns();
                    agGridApi.current = params.api;
                  }}
                />
              )}
            </TableContainer>
            {recordCount ? (
              <TablePagination
                backIconButtonProps={{
                  'aria-label': 'Previous Page',
                }}
                component="div"
                count={recordCount}
                nextIconButtonProps={{
                  'aria-label': 'Next Page',
                }}
                page={page - 1}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 2000]}
                onChangePage={(event, page) => {
                  setPage(page + 1);
                  setSelectedItems(prev => [...prev, ...selectedItemsByPageRef.current || []]);
                  selectedItemsByPageRef.current = [];
                  setTimeout(performSearch, 200);
                }}
                onChangeRowsPerPage={({ target }) => {
                  const rows = target.value;
                  setRowsPerPage(rows);
                  setSelectedItems(prev => [...prev, ...selectedItemsByPageRef.current || []]);
                  selectedItemsByPageRef.current = [];
                  // setTimeout(performSearch, 200);
                }}
              />
            ) : null}
          </>
        ) : selectedPropertiesRef.current?.length > 0 && saleSelectShow ? (
          <>
            <TableContainer className="ag-theme-material">
              {loadingSales ? (
                <div style={{ textAlign: 'center' }}>
                  <CircularProgress />
                </div>
              ) : (
                <AgGrid
                  name="selectPropertySales"
                  rowHeight={38}
                  columnDefs={buildColumnDefsSale()}
                  rowData={propertySales}
                  fetchPreference={fetchPreference}
                  persistPreference={persistPreference}
                  preference={preference}
                  enableBrowserTooltips
                  defaultColDef={{ resizable: true, sortable: true }}
                  onSelectionChanged={handleSelectionSaleChanged}
                  onRowDataUpdated={param => onSaleRowDataChanged(param)}
                  rowSelection={'single'}
                />
              )}
            </TableContainer>
          </>
        ) : (
          <>
            <TableContainer className="ag-theme-material">
              {state === fetchingState.FETCHING ? (
                <div style={{ textAlign: 'center' }}>
                  <CircularProgress />
                </div>
              ) : (
                <AgGrid
                  name="propertyLookup"
                  rowHeight={38}
                  columnDefs={buildColumnDefs()}
                  rowData={buildRowData || []}
                  fetchPreference={fetchPreference}
                  persistPreference={persistPreference}
                  preference={preference}
                  enableBrowserTooltips
                  defaultColDef={{ resizable: true, sortable: true }}
                  onSelectionChanged={handleSelectionChanged}
                  rowSelection={
                    mode === propertyLookupModes.singleSelection
                      ? 'single'
                      : 'multiple'
                  }
                  rowMultiSelectWithClick={
                    mode === propertyLookupModes.multiSelection
                  }
                  onGridReady={params => {
                    params.columnApi.autoSizeAllColumns();
                    agGridApi.current = params.api;
                  }}
                  onRowDataUpdated={param => onRowDataUpdated(param)}
                />
              )}
            </TableContainer>
            {recordCount ? (
              <TablePagination
                backIconButtonProps={{
                  'aria-label': 'Previous Page',
                }}
                component="div"
                count={recordCount}
                nextIconButtonProps={{
                  'aria-label': 'Next Page',
                }}
                page={page - 1}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 2000]}
                onChangePage={(event, page) => {
                  setPage(page + 1);
                  setSelectedItems(prev => [...prev, ...selectedItemsByPageRef.current || []]);
                  selectedItemsByPageRef.current = [];
                  setTimeout(performSearch, 200);
                }}
                onChangeRowsPerPage={({ target }) => {
                  const rows = target.value;
                  setRowsPerPage(rows);
                  setSelectedItems(prev => [...prev, ...selectedItemsByPageRef.current || []]);
                  selectedItemsByPageRef.current = [];
                  // setTimeout(performSearch, 200);
                }}
              />
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeProcedure} color="primary">
          Cancel
        </Button>
        {marketLookup ? (
          selectedPropertiesRef.current?.length > 0 && saleSelectShow ? (
            <Button
              color="primary"
              disabled={selectedSaleRef.current?.length === 0}
              onClick={SelectSales}
            >
              Select
            </Button>
          ) : (
            <Button
              color="primary"
              disabled={selectedPropertiesRef.current?.length === 0}
              onClick={nextProcedure}
            >
              Next
            </Button>
          )
        ) : (
          <Button onClick={submitValues.bind(null, [])} color="primary">
            Select
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

PropertyLookupModal.propTypes = {
  addPropertyToBP: PropTypes.func,
  addPropertyToSG: PropTypes.func,
  assessmentService: PropTypes.any,
  config: PropTypes.arrayOf(PropTypes.object),
  currentAppraisalYear: PropTypes.string,
  dropdowns: ImmutablePropTypes.map,
  modalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  userData: ImmutablePropTypes.map,
  fetchDropdowns: PropTypes.func.isRequired,
  fetchSearchConfig: PropTypes.func.isRequired,
  fetchSearchResultsConfig: PropTypes.func.isRequired,
  fetchPreference: PropTypes.func.isRequired,
  persistPreference: PropTypes.func.isRequired,
  lookupProperty: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object),
  preference: PropTypes.object,
  mode: PropTypes.number,
  marketLookup: PropTypes.bool,
  currentValuation: PropTypes.any,
  searchParams: PropTypes.array,
  primarySearchFields: PropTypes.arrayOf(PropTypes.object),
  state: PropTypes.string,
  data: ImmutablePropTypes.list,
  recordCount: PropTypes.number,
  selectProperties: PropTypes.func.isRequired,
  values: ImmutablePropTypes.map,
  primaryResultFields: PropTypes.arrayOf(PropTypes.object),
  fetchAppraisalYears: PropTypes.func.isRequired,
  appraisalYears: PropTypes.any,
  fetchPropertySalesListing: PropTypes.func.isRequired,
  fetchMarketApproachValuation: PropTypes.func.isRequired,
  fetchMarketApproachCompImages: PropTypes.func.isRequired,
  marketApproachManualReset: PropTypes.func.isRequired,
  marketApproachAddCompList: PropTypes.func.isRequired,
  marketApproach: PropTypes.any,
  futureYear: PropTypes.any,
};

PropertyLookupModal.defaultProps = {
  mode: 0,
  marketLookup: false,
};

const CloseButton = styled(IconButton)`
  &&& {
    position: absolute;
    right: 10px;
    top: 10px;
    color: #888;
  }
`;

const TableContainer = styled.div`
  height: 450px;
  width: 100%;
  margin: 10px 4px 4px 4px;
`;

const mapDispatchToProps = {
  fetchPreference: globalActions.fetchPreference,
  persistPreference: globalActions.persistPreference,
  fetchSearchConfig: propertyActions.fetchSearchConfig,
  fetchSearchResultsConfig: propertyActions.fetchSearchResultsConfig,
  lookupProperty: propertyActions.lookupProperty,
  selectProperties: propertyActions.propertyLookupModalSelectRecords,
  closeModal: propertyActions.closePropertyLookupModal,
  fetchDropdowns: propertyActions.fetchPropertyDropdowns,
  fetchAppraisalYears: globalActions.fetchAppraisalYears,
  fetchPropertySalesListing: propertyActions.fetchPropertySalesListing,
  fetchMarketApproachValuation: propertyActions.fetchMarketApproachValuation,
  fetchMarketApproachCompImages: propertyActions.fetchMarketApproachCompImages,
  marketApproachManualReset: propertyActions.marketApproachManualReset,
  marketApproachAddCompList: propertyActions.marketApproachAddCompList,
};

const enhance = compose(
  connect(
    selector,
    mapDispatchToProps,
  ),
);

export default enhance(PropertyLookupModal);
